import React from 'react';
import keyBy from 'lodash/keyBy';

import { TFunction } from '@lyearn/i18n';
import { Dollar, Euro, Hash, Percent, Pound, Rupee, Yen } from '@lyearn/icons';
import { inputStyles } from '@/components/EditorBuilder/Stats/styles';
import { Field, FieldType } from '@/components/Form';
import { renderMediumOptionLabel } from '@/components/Form/components/Select/optionLabelRenderer';
import { renderMediumSelectValue } from '@/components/Form/components/Select/renderValue';
import { FORM_PICKLIST_PROPS } from '@/modules/corporate/components/picklist';
import { DatabaseFieldEditorConfig } from '@/modules/corporate/modules/database/fields/types';
import { DatabaseNumberFormatEnum } from '@/types/schema';

import * as FieldUtils from '../../utils/editor/config';
import { fieldSettingsSectionStyles } from '../../utils/editor/styles';

const getNumberFormatOptions = (t: TFunction) => [
  {
    Icon: Hash,
    label: t('Database:NumberFormats.Number'),
    value: DatabaseNumberFormatEnum.Number,
  },
  {
    Icon: Percent,
    label: t('Database:NumberFormats.Percentage'),
    value: DatabaseNumberFormatEnum.Percentage,
  },
  {
    Icon: Rupee,
    label: t('Database:NumberFormats.Rupee'),
    value: DatabaseNumberFormatEnum.Rupee,
  },
  {
    Icon: Dollar,
    label: t('Database:NumberFormats.Dollar'),
    value: DatabaseNumberFormatEnum.Dollar,
  },
  {
    Icon: Euro,
    label: t('Database:NumberFormats.Euro'),
    value: DatabaseNumberFormatEnum.Euro,
  },
  {
    Icon: Yen,
    label: t('Database:NumberFormats.Yen'),
    value: DatabaseNumberFormatEnum.Yen,
  },

  {
    Icon: Pound,
    label: t('Database:NumberFormats.Pound'),
    value: DatabaseNumberFormatEnum.Pound,
  },
];

const NumberFieldEditorConfig: DatabaseFieldEditorConfig = {
  getFieldSettingsFormProps: ({ t, allowPartialUpdates }) => {
    const numberFormatOptions = getNumberFormatOptions(t);

    const fields: Field[] = [
      FieldUtils.getDatabaseTypeFormField(t, allowPartialUpdates),
      {
        name: 'metadataField.numberFormat',
        label: t('Common:Filter.Type'),
        type: FieldType.Picklist,
        picklistValues: numberFormatOptions,
        props: {
          className: FORM_PICKLIST_PROPS.className,
          optionLabelRenderer: renderMediumOptionLabel,
          renderValue: renderMediumSelectValue,
        },
      },
      FieldUtils.getRequiredFormField(t),
      FieldUtils.getDescriptionEnabledFormField(t),
    ];

    return {
      fields: keyBy(fields, 'name'),
      sectionLayout: [
        {
          id: 'number-type-settings',
          className: fieldSettingsSectionStyles(32),
          layout: ['metadataField.type', 'metadataField.numberFormat'],
        },
        {
          id: 'number-field-view-settings',
          className: fieldSettingsSectionStyles(24),
          layout: ['viewField.required', 'viewField.descriptionEnabled'],
        },
      ],
      overrides: FieldUtils.FormOverrides,
    };
  },
  getFieldSettingsFormInitialValues: ({ metadataField, viewField }) => ({
    metadataField: { type: metadataField.type, numberFormat: metadataField.numberFormat },
    viewField: { required: viewField.required, descriptionEnabled: viewField.descriptionEnabled },
  }),
  adaptEditorFormField: ({ field, t }) => {
    const numberFormat = field.metadataField.numberFormat;
    const Icon = numberFormat
      ? getNumberFormatOptions(t).find(({ value }) => value === numberFormat)?.Icon
      : null;

    return {
      size: 'large',
      placeholder: t('Database:WriteYourAnswer'),
      props: {
        className: inputStyles,
        endAdornment: Icon && <Icon className="h-20 w-20" fillColorToken="icon-placeholder" />,
      },
    };
  },
};

export default NumberFieldEditorConfig;
